import React from "react";
import { Box, Button, Typography, Stack, Tooltip, Zoom } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const SessionControls = ({
  user,
  isTrackerEnabled,
  AccSession,
  handleStart,
  handleEnd,
}) => {
  if (user?.userType !== "Therapist" || !isTrackerEnabled) return null;
  const getDuration = (start, end) => {
    if (!start || !end) return "0m";
    const startTime = new Date(start);
    const endTime = new Date(end);
    const diffInMs = endTime - startTime;
    const diffInMins = Math.round(diffInMs / 60000);

    if (diffInMins < 60) return `${diffInMins}m`;
    const hours = Math.floor(diffInMins / 60);
    const mins = diffInMins % 60;
    return `${hours}h ${mins}m`;
  };

  const commonPaperProps = {
    elevation: 0,
    sx: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 1.5,
      p: "10px 24px",
      borderRadius: "30px",
      border: "1px solid",
      backdropFilter: "blur(5px)",
      transition: "all 0.3s ease-in-out",
      width: "fit-content",
    },
  };

  const containerProps = {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    py: 1.5, // slightly more vertical padding
  };

  // --- 1. STATE: COMPLETED ---
  if (AccSession.endTime) {
    return (
      <Box sx={containerProps}>
        <Tooltip
          TransitionComponent={Zoom}
          title={`Started: ${new Date(AccSession.admissionTime).toLocaleTimeString()}`}
          arrow
        >
          <Stack
            direction="row"
            {...commonPaperProps}
            sx={{
              ...commonPaperProps.sx,
              bgcolor: "rgba(240, 253, 244, 0.7)", // light green tint
              borderColor: "#bbf7d0", // soft green border
              boxShadow: "0 2px 8px rgba(22, 163, 74, 0.08)",
              cursor: "help",
            }}
          >
            <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 22 }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: "#166534",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Completed
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#166534",
                display: "flex",
                alignItems: "center",
                gap: 0.7,
                fontWeight: 600,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 18 }} />
              {getDuration(AccSession.admissionTime, AccSession.endTime)}
            </Typography>
          </Stack>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={containerProps}>
      {!AccSession.isAdmitted ? (
        <Button
          variant="outlined" 
          startIcon={<PlayArrowIcon sx={{ fontSize: "24px !important" }} />}
          onClick={() => handleStart(AccSession.id)}
          sx={{
            ...commonPaperProps.sx,
            color: "#00ff62",
            bgcolor: "rgba(76, 175, 79, 0.01)", 
            borderColor: "#81c784", 
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 800,
            "&:hover": {
              bgcolor: "rgba(76, 175, 80, 0.2)",
              borderColor: "#66bb6a",
              boxShadow: "0 4px 12px rgba(76, 175, 80, 0.2)",
            },
          }}
        >
          Start Patient Session
        </Button>
      ) : (
        <Button
          variant="outlined"
          startIcon={<StopIcon sx={{ fontSize: "24px !important" }} />}
          onClick={() => handleEnd(AccSession.id)}
          sx={{
            ...commonPaperProps.sx,
            color: "#ff0000",
            bgcolor: "rgba(255, 111, 101, 0)", 
            borderColor: "#ff0000", 
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 800,
            "&:hover": {
              bgcolor: "rgba(244, 67, 54, 0.2)",
              borderColor: "#750101",
              boxShadow: "0 4px 12px rgba(244, 67, 54, 0.2)",
            },
          }}
        >
          End Active Session
        </Button>
      )}
    </Box>
  );
};

export default SessionControls;
